import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PartyPopper, ArrowRight, Image as ImageIcon } from 'lucide-react';
import { ROUTES } from '@/lib/data';
import { publicApi } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import { fadeUp } from '@/lib/motion';
import { ReservationButton } from '@/components/public/ReservationButton';

export const CelebrateCTASection = () => {
    const [media, setMedia] = useState([]);
    const onCumple = () => trackEvent('cta_click', window.location.pathname, { from: 'celebrate_cta', cta: 'ver_cumple' });

    useEffect(() => {
        Promise.all([
            publicApi.getMedia('events').catch(() => []),
            publicApi.getMedia('gallery').catch(() => []),
        ]).then(([eventMedia, galleryMedia]) => {
            const merged = [...(Array.isArray(eventMedia) ? eventMedia : []), ...(Array.isArray(galleryMedia) ? galleryMedia : [])]
                .filter((m) => m?.url)
                .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
            setMedia(merged.slice(0, 3));
        });
    }, []);

    const cards = useMemo(() => {
        const styles = [
            'top-0 left-0 w-44 h-44 sm:w-56 sm:h-56 rotate-[-6deg] border-white',
            'top-12 right-0 w-52 h-52 sm:w-64 sm:h-64 rotate-[5deg] border-[hsl(var(--kche-yellow))]',
            'bottom-0 left-10 w-48 h-48 sm:w-60 sm:h-60 rotate-[-3deg] border-[hsl(var(--kche-cyan))]',
        ];

        return styles.map((style, index) => ({ style, media: media[index] }));
    }, [media]);

    return (
        <section data-testid="home-celebrate-cta" className="relative overflow-hidden bg-gradient-fun text-white py-16 sm:py-20 lg:py-24">
            <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-[hsl(var(--kche-yellow))]/30 blur-3xl animate-blob" aria-hidden="true" />
            <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-[hsl(var(--kche-cyan))]/30 blur-3xl animate-blob" style={{ animationDelay: '4s' }} aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
                <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={fadeUp}>
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur border border-white/30 px-3 py-1 text-xs font-bold"><PartyPopper className="size-3.5" /> Tu próxima aventura</span>
                    <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-[1.05]">Celebra, juega y vive la <span className="text-[hsl(var(--kche-yellow))]">aventura</span> en Pifo</h2>
                    <p className="mt-4 text-lg text-white/95 max-w-xl">Reserva tu visita, cumpleaños o evento. Coordinamos los detalles para que solo te preocupes por divertirte.</p>
                    <div className="mt-7 flex flex-wrap items-center gap-3">
                        <ReservationButton
                            from="celebrate_cta"
                            defaults={{ reservation_type: 'cumpleanos' }}
                            size="md"
                            tone="yellow"
                            testId="celebrate-cta-reserva"
                        />
                        <Link to={ROUTES.cumple} onClick={onCumple} data-testid="celebrate-cta-cumple" className="inline-flex items-center gap-2 rounded-full bg-white text-[hsl(var(--kche-pink))] font-bold font-display px-7 py-3.5 text-base shadow-pop hover:scale-[1.03] transition">Ver cumpleaños y eventos <ArrowRight className="size-5" /></Link>
                    </div>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="relative h-[360px] sm:h-[420px]">
                    {cards.map(({ style, media: item }, index) => (
                        <div key={index} className={`absolute ${style} rounded-3xl overflow-hidden border-4 shadow-pop bg-white/20 backdrop-blur`}>
                            {item?.url ? (
                                <img src={item.url} alt={item.alt || ''} className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-white/20">
                                    <ImageIcon className="size-10 text-white/75" />
                                </div>
                            )}
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default CelebrateCTASection;
